import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useTranslation } from 'react-i18next';
import { useStorefrontContext } from 'context/StorefrontContext';

type Props = {
  contrast?: boolean;
};

const ColorModeToggle: React.FC<Props> = ({ contrast = false }) => {
  const { t } = useTranslation('common');
  const { colorMode, setColorMode } = useStorefrontContext();

  const labelSx = { display: { xs: 'none', sm: 'inline' }, ml: 0.5 };

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={colorMode}
      onChange={(_, value: 'light' | 'dark' | null) => {
        if (value) setColorMode(value);
      }}
      aria-label={t('nav.colorMode')}
      sx={{
        flexShrink: 0,
        bgcolor: contrast ? 'rgba(255,255,255,0.16)' : 'background.paper',
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          px: { xs: 0.75, sm: 1.25 },
          py: 0.4,
          color: contrast ? 'inherit' : 'text.primary',
          borderColor: contrast ? 'rgba(255,255,255,0.45)' : 'divider',
          '&.Mui-selected': {
            color: contrast ? 'primary.main' : 'primary.contrastText',
            bgcolor: contrast ? 'common.white' : 'primary.main',
            '&:hover': {
              bgcolor: contrast ? 'grey.100' : 'primary.dark',
            },
          },
        },
      }}
    >
      <ToggleButton value="light" aria-label={t('nav.lightModeLabel')}>
        <LightModeOutlinedIcon sx={{ fontSize: 16 }} />
        <Box component="span" sx={labelSx}>
          {t('nav.light')}
        </Box>
      </ToggleButton>
      <ToggleButton value="dark" aria-label={t('nav.darkModeLabel')}>
        <DarkModeOutlinedIcon sx={{ fontSize: 16 }} />
        <Box component="span" sx={labelSx}>
          {t('nav.dark')}
        </Box>
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

export default ColorModeToggle;
